"use client"

import { Images } from "@/constants/images"
import { t } from "@/languages/i18n"
import { CalendarOutlined, EyeOutlined, KeyOutlined, TranslationOutlined } from "@ant-design/icons"
import { Button, Input, Select, Typography } from "antd"
import dayjs from "dayjs"
import Image from "next/image"
import { useState } from "react"

const { Text } = Typography

interface StatusPublishProps {
  setStatus: (status: string) => void
  status: string
}

export const StatusPublish = ({ setStatus, status }: StatusPublishProps) => {
  const handleChange = (value: any) => {
    setStatus(value.value)
  }

  return (
    <div>
      <Text className="text-sm">
        <KeyOutlined /> {t("Trạng thái")}:
        <Select
          labelInValue
          className="w-fit ml-2"
          onChange={handleChange}
          options={[
            { value: "Draft", label: t("Bản nháp") },
            { value: "Published", label: t("Xuất bản") },
          ]}
          value={{ value: status, label: status === "Published" ? t("Xuất bản") : t("Bản nháp") }}
        />
      </Text>
    </div>
  )
}

export const LanguagePublish = ({ language, setLanguages, isCreateEdit, existingLanguages = [] }: any) => {
  const handleChange = (value: any) => {
    setLanguages(value.value)
  }

  const languageOptions = [
    {
      value: "vi",
      disabled: existingLanguages.includes("vi"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoVi} alt="Vietnamese" width={20} height={20} className="w-5 h-5" />
          {t("VI")}
        </div>
      ),
    },
    {
      value: "en",
      disabled: existingLanguages.includes("en"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoEn} alt="English" width={20} height={20} className="w-5 h-5" />
          {t("EN")}
        </div>
      ),
    },
    {
      value: "cs",
      disabled: existingLanguages.includes("cs"),
      label: (
        <div className="flex items-center gap-2">
          <Image src={Images.logoCs} alt="Czech" width={20} height={20} className="w-5 h-5" />
          {t("CS")}
        </div>
      ),
    },
  ]

  const selectedOption = languageOptions.find(option => option.value === language)

  return (
    <div>
      <Text className="text-sm space-x-3">
        <TranslationOutlined /> {t("Ngôn ngữ")}:
        <Select
          labelInValue
          className="w-fit"
          onChange={handleChange}
          options={languageOptions}
          value={selectedOption ? { value: selectedOption.value, label: selectedOption.label } : undefined}
          disabled={!isCreateEdit}
          loading={!language}
        />
      </Text>
    </div>
  )
}

export const VisibilityPublish = ({ status, onView }: { status?: string; onView?: () => void }) => {
  const isPublished = status === "Publish" || status === "Published"

  return (
    <div>
      <Text className="text-sm">
        <EyeOutlined /> {t("Hiển thị")}:
        <span className="ml-2">{isPublished ? t("Công khai") : t("Bản nháp")}</span>
        {/* {isPublished && (
          <span className="text-blue-500 underline cursor-pointer ml-2" onClick={onView}>
            {t("Xem")}
          </span>
        )} */}
      </Text>
    </div>
  )
}

export const DatePublish = ({ setPublishDate }: { setPublishDate: (date: string) => void }) => {
  const now = dayjs()
  const [isEditing, setIsEditing] = useState(false)
  const [isPublished, setIsPublished] = useState(false)
  const [publishedDate, setPublishedDateDisplay] = useState<string | null>(null)
  const [tempDate, setTempDate] = useState({
    month: now.format("MMM"),
    day: now.format("DD"),
    year: now.format("YYYY"),
    hour: now.format("HH"),
    minute: now.format("mm"),
  })

  const handleEditClick = () => setIsEditing(true)
  const handleCancelClick = () => setIsEditing(false)

  const handleDateChange = (key: string, value: string) => {
    setTempDate(prev => ({ ...prev, [key]: value }))
  }

  const handleOkClick = () => {
    const formattedDate = `${tempDate.month} ${tempDate.day}, ${tempDate.year} at ${tempDate.hour}:${tempDate.minute}`
    const isoDate = dayjs(`${tempDate.year}-${tempDate.month}-${tempDate.day} ${tempDate.hour}:${tempDate.minute}`).toISOString()
    setPublishedDateDisplay(formattedDate)
    setPublishDate(isoDate)
    setIsEditing(false)
    setIsPublished(true)
  }

  return (
    <div>
      <Text className="text-sm">
        <CalendarOutlined /> {t("Xuất bản lúc")}:
        <span className="ml-2 text-xs">
          {isPublished ? publishedDate : now.format("DD/MM/YYYY HH:mm")}
        </span>
        {/* {!isEditing && (
          <span className="text-blue-500 underline cursor-pointer ml-2" onClick={handleEditClick}>
            {t("Sửa")}
          </span>
        )} */}
      </Text>
      {isEditing && (
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center gap-1 flex-wrap">
            <Select
              defaultValue={tempDate.month}
              className="w-[80px]"
              onChange={value => handleDateChange("month", value)}
              options={[
                { value: "Jan", label: t("T1") },
                { value: "Feb", label: t("T2") },
                { value: "Mar", label: t("T3") },
                { value: "Apr", label: t("T4") },
                { value: "May", label: t("T5") },
                { value: "Jun", label: t("T6") },
                { value: "Jul", label: t("T7") },
                { value: "Aug", label: t("T8") },
                { value: "Sep", label: t("T9") },
                { value: "Oct", label: t("T10") },
                { value: "Nov", label: t("T11") },
                { value: "Dec", label: t("T12") },
              ]}
            />
            <Input value={tempDate.day} onChange={e => handleDateChange("day", e.target.value)} className="w-[40px]" />
            <Input value={tempDate.year} onChange={e => handleDateChange("year", e.target.value)} className="w-[60px]" />
            <span>{t("lúc")}</span>
            <Input value={tempDate.hour} onChange={e => handleDateChange("hour", e.target.value)} className="w-[40px]" />
            <span>:</span>
            <Input value={tempDate.minute} onChange={e => handleDateChange("minute", e.target.value)} className="w-[40px]" />
          </div>
          <div className="flex gap-2">
            <Button size="small" type="primary" onClick={handleOkClick}>{t("Đồng ý")}</Button>
            <Button size="small" onClick={handleCancelClick}>{t("Hủy")}</Button>
          </div>
        </div>
      )}
    </div>
  )
}